import { useRef } from "react";
import type { SchemaStageState } from "./useSchemaStage";

// Под този праг (пиксели) натискането е „докосване", не местене на платното.
const PAN_THRESHOLD_PX = 4;
// Кликът, който браузърът праща веднага след местене, идва в този прозорец (ms).
// По-късен клик е нов и минава — при пръст клик след местене често няма.
const CLICK_AFTER_PAN_MS = 250;

type PanSession = {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  moved: boolean;
};

/**
 * Местене на платното с влачене по празното място (мишка, писалка, пръст).
 * Елементите, лентите и поповърите спират `pointerdown` сами, затова тук
 * стига само натискане по пода — и по маса в резервациите (тя не го спира):
 * там докосването си остава клик, а влаченето мести платното. Pointer capture
 * се взима чак след прага — иначе кликът би отишъл на платното, не на масата.
 * Докосване без местене вика `onTap` (маха избора / затваря поповъра) —
 * и върху маса; събитието казва къде е било.
 */
export const useCanvasPan = (
  stage: SchemaStageState,
  onTap?: (event: React.PointerEvent<HTMLDivElement>) => void,
) => {
  const sessionRef = useRef<PanSession | null>(null);
  // Кога е свършило последното местене — кликът веднага след него не бива да стигне до маса под пръста.
  const panEndedAtRef = useRef(0);

  return {
    handlers: {
      onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.pointerType === "mouse" && event.button !== 0 && event.button !== 1) return;
        sessionRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          moved: false,
        };
      },
      onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
        const session = sessionRef.current;
        if (!session || session.pointerId !== event.pointerId) return;
        if (!session.moved) {
          const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
          if (distance < PAN_THRESHOLD_PX) return;
          if (!stage.canPan) {
            sessionRef.current = null;
            return;
          }
          session.moved = true;
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        stage.panBy(event.clientX - session.lastX, event.clientY - session.lastY);
        session.lastX = event.clientX;
        session.lastY = event.clientY;
      },
      onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
        const session = sessionRef.current;
        if (!session || session.pointerId !== event.pointerId) return;
        sessionRef.current = null;
        if (session.moved) panEndedAtRef.current = performance.now();
        else onTap?.(event);
      },
      onPointerCancel: () => {
        sessionRef.current = null;
      },
      onClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
        if (performance.now() - panEndedAtRef.current > CLICK_AFTER_PAN_MS) return;
        event.stopPropagation();
      },
      // Същото за двойния клик (отваря маса в „Маркиране") — местене, пуснато
      // върху маса, не я отваря.
      onDoubleClickCapture: (event: React.MouseEvent<HTMLDivElement>) => {
        if (performance.now() - panEndedAtRef.current > CLICK_AFTER_PAN_MS) return;
        event.stopPropagation();
      },
    },
  };
};
